import { zodResolver } from '@hookform/resolvers/zod';
import {
  passwordForgotRequestSchema,
  passwordResetRequestSchema,
  type PasswordForgotRequest,
  type PasswordResetRequest,
} from '@homecare/contracts';
import type { z } from 'zod';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';

import { Button, Field } from '@homecare/ui';

import { ErrorNote } from '../../components/primitives';
import { request } from '../../lib/api';
import { useUnsavedChangesGuard } from '../../lib/unsaved-changes';

import { AUTH_LINK, AuthAside, AuthFrame } from './auth-frame';

/**
 * Request a reset link (§7).
 *
 * The acknowledgement is the same whether or not the address exists, because
 * the API answers the same either way — and the copy has to match, or the
 * screen reintroduces the enumeration oracle the API was careful to close.
 * "If that address is registered" is doing real work in that sentence.
 */
export function ForgotPassword(): ReactNode {
  const [sent, setSent] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  // Input type, not output — `audience` has a `.default()`. See sign-in.tsx.
  const form = useForm<z.input<typeof passwordForgotRequestSchema>, unknown, PasswordForgotRequest>(
    {
      resolver: zodResolver(passwordForgotRequestSchema),
      defaultValues: { email: '', audience: 'staff' },
      mode: 'onBlur',
    },
  );

  useUnsavedChangesGuard(form.formState.isDirty && !sent);

  const onSubmit = form.handleSubmit(async (values) => {
    setFailure(null);
    try {
      await request('/v1/auth/password/forgot', { method: 'POST', body: values });
      setSent(true);
    } catch (error) {
      setFailure(error);
    }
  });

  if (sent) {
    return (
      <AuthFrame title="Check your email">
        <p className="text-md text-ink">
          If that address is registered, a reset link is on its way. It works once and expires in 15
          minutes.
        </p>
        <p className="text-ink-3 mt-3 text-sm">
          Nothing arrived? Check the spam folder, then ask an administrator at your agency to
          confirm the address on your account.
        </p>

        <AuthAside>
          <a href="/sign-in" className={AUTH_LINK}>
            Back to sign in
          </a>
        </AuthAside>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame
      title="Reset your password"
      lede="We'll email you a link. It works once and expires in 15 minutes."
    >
      <form noValidate onSubmit={onSubmit} className="grid gap-3">
        <ErrorNote error={failure} nextStep="Try again in a moment." />

        <Field
          label="Email"
          type="email"
          autoComplete="username"
          autoFocus
          error={form.formState.errors.email?.message}
          {...form.register('email')}
        />

        <Button type="submit" className="mt-2 w-full" pending={form.formState.isSubmitting}>
          Send reset link
        </Button>
      </form>

      <AuthAside>
        <a href="/sign-in" className={AUTH_LINK}>
          Back to sign in
        </a>
      </AuthAside>
    </AuthFrame>
  );
}

/**
 * Set a new password (§7).
 *
 * The minimum length lives in `passwordSchema` in packages/contracts, so the
 * inline message under the field and the rule the API enforces are the same
 * sentence from the same object.
 */
export function ResetPassword(): ReactNode {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { token?: string };
  const [failure, setFailure] = useState<unknown>(null);

  const form = useForm<PasswordResetRequest>({
    resolver: zodResolver(passwordResetRequestSchema),
    defaultValues: { token: search.token ?? '', password: '' },
    mode: 'onBlur',
  });

  useUnsavedChangesGuard(form.formState.isDirty && !form.formState.isSubmitSuccessful);

  const onSubmit = form.handleSubmit(async (values) => {
    setFailure(null);
    try {
      await request('/v1/auth/password/reset', { method: 'POST', body: values });
      await navigate({ to: '/sign-in' });
    } catch (error) {
      setFailure(error);
    }
  });

  return (
    <AuthFrame
      title="Choose a new password"
      lede="At least 12 characters, and not one you use anywhere else."
    >
      <form noValidate onSubmit={onSubmit} className="grid gap-3">
        <ErrorNote
          error={failure}
          nextStep="Reset links work once and expire after 15 minutes — request a new one."
        />

        <Field
          label="New password"
          type="password"
          autoComplete="new-password"
          autoFocus
          error={form.formState.errors.password?.message}
          {...form.register('password')}
        />

        <input type="hidden" {...form.register('token')} />

        {form.formState.errors.token && (
          <p className="text-critical text-sm">
            This link is missing its reset code. Request a new link.
          </p>
        )}

        <Button type="submit" className="mt-2 w-full" pending={form.formState.isSubmitting}>
          Save new password
        </Button>
      </form>

      <AuthAside>
        <a href="/sign-in" className={AUTH_LINK}>
          Back to sign in
        </a>
      </AuthAside>
    </AuthFrame>
  );
}
